import { useRoleAuth } from '@src/hooks/useRoleAuth';
import type { CareOrder } from '@talos/kyoko';
import {
  abbreviateId,
  ACTION,
  IconName,
  isTalosUser,
  MixpanelEvent,
  NotificationVariants,
  request,
  useCallbackRef,
  useEndpointsContext,
  useGlobalDialog,
  useGlobalToasts,
  useMixpanel,
  useUserContext,
} from '@talos/kyoko';
import type { MenuItemDef } from 'ag-grid-community';
import { v4 as uuid } from 'uuid';
import { modify } from '../../../components/OMS/CareOrderFormView/CareOrderSlice';
import { closeView, openView } from '../../../components/OMS/OMSSlice';
import { OMSView } from '../../../components/OMS/OMSView';
import { useFeatureFlag } from '../../../hooks';
import { useAppStateDispatch, useAppStateStore } from '../../../providers/AppStateProvider';

interface GetCancelMenuItemParams {
  entity: CareOrder;
  isCancelable: boolean;
}

export function useCareOrderActionMenuItems() {
  const mixpanel = useMixpanel();
  const { user } = useUserContext();
  const { isAuthorized } = useRoleAuth();
  const dispatch = useAppStateDispatch();
  const store = useAppStateStore();
  const { open } = useGlobalDialog();
  const { add: addToast } = useGlobalToasts();
  const { orgApiEndpoint } = useEndpointsContext();
  const { enableCareOrderWriting: enableCareOrderWritingFlag } = useFeatureFlag();

  const enableCareOrderWriting = enableCareOrderWritingFlag || isTalosUser(user);

  const handleForceCancel = useCallbackRef((entity: CareOrder) => {
    const orderTypeLabel = entity.isNetted ? 'Netted Order' : 'Care Order';

    open({
      title: `Force cancel ${orderTypeLabel}`,
      content: (
        <>{`Are you sure you want to force cancel ${orderTypeLabel.toLowerCase()} #${abbreviateId(entity.OrderID)}?`}</>
      ),
      onConfirm: () => {
        mixpanel.track(MixpanelEvent.ForceCancelCareOrder);
        request(
          'PUT',
          entity.isNetted
            ? `${orgApiEndpoint}/care/working/orders/${entity.OrderID}/force-cancel`
            : `${orgApiEndpoint}/care/orders/${entity.OrderID}/force-cancel`
        )
          .then(() => {
            addToast({
              text: `${orderTypeLabel} force cancelled.`,
              variant: NotificationVariants.Positive,
            });
          })
          .catch((e: Error) =>
            addToast({
              text: `Could not force cancel ${orderTypeLabel}: ${e.message}`,
              variant: NotificationVariants.Negative,
            })
          );
      },
    });
  });

  const handleCancel = useCallbackRef((entity: CareOrder) => {
    const orderTypeLabel = entity.isNetted ? 'Netted Order' : 'Care Order';

    open({
      title: `Cancel ${orderTypeLabel}`,
      content: (
        <>
          <p>{`Are you sure you want to cancel ${orderTypeLabel} #${abbreviateId(entity.OrderID)}?`}</p>
        </>
      ),
      onConfirm: () => {
        request(
          'PUT',
          entity.isNetted ? `${orgApiEndpoint}/care/working/orders/cancel` : `${orgApiEndpoint}/care/orders/cancel`,
          {
            ClOrdId: uuid(),
            OrderID: entity.OrderID,
          }
        )
          .then(() => {
            const state = store.getState();
            if (
              state.careOrder.careOrderBeingModified?.OrderID === entity.OrderID &&
              state.OMS.openedView === OMSView.CareOrderForm
            ) {
              dispatch(closeView());
            }

            addToast({
              text: `${orderTypeLabel} cancelled.`,
              variant: NotificationVariants.Positive,
            });
          })
          .catch((e: Error) =>
            addToast({
              text: `Could not cancel ${orderTypeLabel}: ${e.message}`,
              variant: NotificationVariants.Negative,
            })
          );
      },
    });
  });

  const handleModifyOrder = useCallbackRef((entity: CareOrder) => {
    dispatch(modify(entity));
    dispatch(openView(OMSView.CareOrderForm));
  });

  const getModifyMenuItem = useCallbackRef((entity: CareOrder): MenuItemDef | undefined => {
    if (!isAuthorized(ACTION.SUBMIT_CARE_ORDER) || !enableCareOrderWriting || entity.isNetted) {
      return undefined;
    }

    return {
      name: 'Modify',
      disabled: !entity.isModifiable,
      icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
      action: () => handleModifyOrder(entity),
    };
  });

  const getCancelMenuItem = useCallbackRef(
    ({ entity, isCancelable }: GetCancelMenuItemParams): MenuItemDef | undefined => {
      if (!isAuthorized(ACTION.CANCEL_ORDER) || !enableCareOrderWriting) {
        return undefined;
      }

      return {
        name: 'Cancel',
        disabled: !isCancelable,
        icon: `<i class="ag-icon ${IconName.Trash}"/>`,
        action: () => handleCancel(entity),
      };
    }
  );

  const getForceCancelMenuItem = useCallbackRef((entity: CareOrder): MenuItemDef | undefined => {
    if (!isAuthorized(ACTION.FORCE_CANCEL_CARE_ORDER) || !enableCareOrderWriting) {
      return undefined;
    }

    return {
      name: 'Force cancel',
      action: () => handleForceCancel(entity),
    };
  });

  return {
    getModifyMenuItem,
    getCancelMenuItem,
    getForceCancelMenuItem,
  };
}
